import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { navItems } from '../data/mock';
import { Button } from './ui/button';
import DarkModeSwitch from './DarkModeSwitch';

const Navbar = ({ isDarkMode, onToggleDarkMode }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? isDarkMode 
            ? 'bg-gradient-to-r from-[#284139]/95 to-[#111A19]/95 backdrop-blur-xl shadow-2xl shadow-black/30'
            : 'bg-gradient-to-r from-[#284139]/90 to-[#E5D9B6]/90 backdrop-blur-xl shadow-2xl shadow-black/10'
          : 'bg-gradient-to-r from-[#284139]/80 to-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-3 group"
          >
            <img 
              src="/assets/logos/pathways.png" 
              alt="Pathways Africa"
              className="h-28 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`font-medium text-sm tracking-wide transition-all duration-300 relative group ${
                  isDarkMode ? 'text-[#809076] hover:text-[#f5f5f5]' : 'text-[#284139]/70 hover:text-[#284139]'
                }`}
              >
                {item.label}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 ${isDarkMode ? 'bg-[#E5D9B6]' : 'bg-[#284139]'}`} />
              </Link>
            ))}
            
            {/* Dark Mode Switch */}
            <DarkModeSwitch isDark={isDarkMode} onToggle={onToggleDarkMode} />
            
            <Button
              className={`ml-2 px-6 py-2.5 rounded-full border-2 bg-transparent font-medium text-sm btn-transition shadow-lg ${
                isDarkMode 
                  ? 'border-[#E5D9B6] text-[#f5f5f5] hover:bg-[#E5D9B6] hover:text-[#111A19] shadow-[#E5D9B6]/0 hover:shadow-[#E5D9B6]/30'
                  : 'border-[#284139] text-[#284139] hover:bg-[#284139] hover:text-[#E5D9B6] shadow-[#284139]/0 hover:shadow-[#284139]/30'
              }`}
              variant="outline"
            >
              Donate
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2.5 transition-colors rounded-lg ${
              isDarkMode ? 'text-[#809076] hover:text-[#f5f5f5] hover:bg-[#284139]/50' : 'text-[#E5D9B6] hover:text-[#E5D9B6] hover:bg-[#E5D9B6]/10'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'max-h-96 pb-6' : 'max-h-0'
          }`}
        >
          <div className={`flex flex-col space-y-4 pt-4 border-t ${isDarkMode ? 'border-[#284139]' : 'border-[#E5D9B6]/30'}`}>
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`font-medium text-base py-2 transition-colors duration-200 ${
                  isDarkMode ? 'text-[#809076] hover:text-[#f5f5f5]' : 'text-[#E5D9B6] hover:text-[#E5D9B6]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Dark Mode Switch for Mobile */}
            <div className="py-2">
              <DarkModeSwitch isDark={isDarkMode} onToggle={onToggleDarkMode} />
            </div>
            
            <Button
              className={`w-fit px-6 py-2.5 rounded-full border-2 bg-transparent font-medium text-sm btn-transition mt-2 ${
                isDarkMode 
                  ? 'border-[#E5D9B6] text-[#f5f5f5] hover:bg-[#E5D9B6] hover:text-[#111A19]'
                  : 'border-[#E5D9B6] text-[#E5D9B6] hover:bg-[#E5D9B6] hover:text-[#284139]'
              }`}
              variant="outline"
            >
              Donate
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
